import React, { useState, useEffect, useReducer } from 'react'
import { bool, func, string, arrayOf, oneOfType, number } from 'prop-types'
import axios from 'axios'

import {
  CSRF_TOKEN,
  addNotification,
} from '../lib/helpers';
import { defaultApiReducerState, apiReducer } from '../lib/reducer'

import EditModal from './EditModal'
import Notification from './Notification'
import {get} from 'lodash'
import className from 'classnames'

const BulkEditInformationModal = ({
  isActive,
  title,
  onClose,
  updatedIds,
  updatedAtField,
  templateApiUrl,
  bulkUpdateApiUrl
}) => {
  const [templateList, templateDispatch] = useReducer(apiReducer, defaultApiReducerState)
  const [editInformation, editInformationDispatch] = useReducer(apiReducer, defaultApiReducerState)
  const [isDropDownActive, setDropDownActive] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState({})

  const onSaveTemplate = (templateName, text) => {
    return axios.post(templateApiUrl, {
      template: {
        name: templateName,
        text: text,
      },
      authenticity_token: CSRF_TOKEN,
    })
      .then((res) => {
        if (get(res, 'data.id')) {
          templateDispatch({
            type: 'SUCCESS',
            payload: [
              ...templateList.data || [],
              get(res, 'data'),
            ]
          })
          setSelectedTemplate(res.data)
        }
      })
      .catch(e => e.message)
  }

  const onDeleteTemplate = (id) => {
    axios.delete(`${templateApiUrl}/${id}`, {
      data: {
        authenticity_token: CSRF_TOKEN,
      },
    })
      .then(() => {
        templateDispatch({
          type: 'SUCCESS',
          payload: templateList.data.filter((template) => template.id !== id )
        })
      })
  }

  const onBulkEditInformation = (text) => {
    editInformationDispatch({ type: 'REQUEST' })
    axios.put(bulkUpdateApiUrl, {
      ids: updatedIds,
      [updatedAtField]: text,
      authenticity_token: CSRF_TOKEN,
    })
      .then((res) => {
        if (res.status === 200) {
          addNotification({
            message: get(res, 'data.message', 'Processing post update'),
            type: Notification.NOTIFICATION_TYPES.success,
          })
        }
      })
      .catch((error) => {
        addNotification({
          message: get(error, 'response.data.message'),
          type: Notification.NOTIFICATION_TYPES.error,
        })
      })
      .then(() => {
        // We need to delay this because it's on a worker.
        // So it will not update immediately.
        window.setTimeout(() => {
          window.location.reload()
        }, 3000)
      })
  }

  useEffect(() => {
    templateDispatch({ type: 'REQUEST' })
    axios.get(templateApiUrl)
      .then((res) => {
        templateDispatch({ type: 'SUCCESS', payload: get(res, 'data') })
      })
      .catch((e) => templateDispatch({ type: 'FAILURE', payload: e.error }))
  }, [])

  return (
    <EditModal
      isActive={isActive}
      title={title}
      onClose={onClose}
      initialValues={{ text: '', saveAsNewTemplate: false, templateName: '' }}
      onSubmit={({ text }) => onBulkEditInformation(text)}
      isSubmitting={editInformation.isRequest}
    >
      {(form) => {
        return (
          <div className='BulkEditInformationModal'>
            <div className='BulkEditInformationModal__select'>
              <a
                className='Select'
                onClick={() => {
                  setDropDownActive(!isDropDownActive)
                }}
              >
                {get(selectedTemplate, 'text') ?
                  <div className='BulkEditInformationModal__select__panel__dl__item'>
                    <dt>{get(selectedTemplate, 'name')}</dt>
                    <dd>{get(selectedTemplate, 'text')}</dd>
                  </div>
                  :
                  <span className='BulkEditInformationModal__select__span'>Select template</span>
                }
              </a>
              <div
                className={className('BulkEditInformationModal__select__panel', {
                  'BulkEditInformationModal__select__panel--active': isDropDownActive,
                })}
              >
                <h3 className='BulkEditInformationModal__select__panel__h3'>Select template</h3>

                {(templateList.data || []).length === 0 &&
                <p className='BulkEditInformationModal__select__panel__p'>No template yet.</p>
                }

                <dl className='BulkEditInformationModal__select__panel__dl'>
                  {(templateList.data || []).map((template, i) => (
                    <a
                      key={i}
                      className='BulkEditInformationModal__select__panel__dl__item'
                      onClick={() => {
                        setSelectedTemplate(template)
                        form.values.text = template.text
                        form.saveAsNewTemplate = false
                        setDropDownActive(false)
                      }}
                    >
                      <dt>{template.name}</dt>
                      <dd>{template.text}</dd>
                      <span
                        className='BulkEditInformationModal__select__panel__dl__item__span'
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('Are you sure you want to delete?')) {
                            onDeleteTemplate(template.id)
                          }
                        }}
                      />
                    </a>
                  ))}
                </dl>
              </div>
            </div>
            <textarea
              className='Input'
              rows={5}
              name="text"
              value={form.values.text}
              onChange={(e) => {
                if (get(selectedTemplate, 'text')) {
                  setSelectedTemplate({})
                }

                if (e.target.value.length === 0) {
                  form.values.saveAsNewTemplate = false
                  form.values.templateName = ''
                }

                form.handleChange(e)
              }}
            />
            <div
              className='BulkEditInformationModal__save'
              disabled={get(selectedTemplate, 'text') || form.values.text === ''}
            >
              <label className='BulkEditInformationModal__save__label'>
                <input
                  type="checkbox"
                  name="saveAsNewTemplate"
                  checked={form.values.saveAsNewTemplate}
                  onChange={form.handleChange}
                />

                <span className='CheckBox' />
                <span className='BulkEditInformationModal__save__label__span'>Save as new template</span>
              </label>

              <div
                className={className('BulkEditInformationModal__save__inputs', {
                  'ChangeBioModal__save__inputs--active': form.values.saveAsNewTemplate,
                })}
              >
                <input
                  id="templateName"
                  type='text'
                  className='Input'
                  placeholder='Template name'
                  value={form.values.templateName}
                  onChange={form.handleChange}
                />
                <a
                  className='Button'
                  disabled={form.values.templateName === ''}
                  onClick={() => {
                    onSaveTemplate(form.values.templateName, form.values.text)
                    form.values.saveAsNewTemplate = false
                    form.values.templateName = ''
                  }}
                >
                  Save
                </a>
              </div>
            </div>
          </div>
        )
      }}
    </EditModal>
  )
}

BulkEditInformationModal.propTypes = {
  isActive: bool.isRequired,
  onClose: func.isRequired,
  updatedAtField: string.isRequired,
  templateApiUrl: string.isRequired,
  bulkUpdateApiUrl: string.isRequired,
  title: string,
  updatedIds: arrayOf(oneOfType([string, number])),
}

export default BulkEditInformationModal
